import { buildUser, buildAlbumRaw } from 'helpers'
import { readAlbumPage, saveAlbumPage } from 'scanProgress'

const API_URL = 'https://api.spotify.com/v1'
// Optional same-origin proxy makes Retry-After readable when Spotify omits CORS exposure.
const API_PROXY = process.env.REACT_APP_SPOTIFY_API_PROXY || ''
const HTTP_TOO_MANY_REQUESTS = 429

const COOLDOWN_KEY = 'spotify-release-list:retry-after'
const DEFAULT_RETRY_SECONDS = 15 * 60
let retryAt = 0
let retryTimeKnown = false
let rateLimitDetails = null

// Keep the cooldown across reloads and share it with other tabs on this origin.
function readRetryAt() {
  try {
    const stored = JSON.parse(localStorage.getItem(COOLDOWN_KEY))
    // Older versions stored only a timestamp, without knowing if it was a fallback.
    const storedTime = typeof stored === 'number' ? stored : stored?.retryAt
    if (Number.isFinite(storedTime) && storedTime >= retryAt) {
      retryAt = storedTime
      retryTimeKnown = stored?.retryTimeKnown === true
      rateLimitDetails = stored?.details || null
    }
  } catch {}
  return retryAt
}

function rateLimitError(local = false) {
  const seconds = Math.max(1, Math.ceil((retryAt - Date.now()) / 1000))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remaining = hours
    ? `${hours}h ${minutes}m`
    : minutes
      ? `${minutes}m ${seconds % 60}s`
      : `${seconds}s`
  const error = new FetchError(
    HTTP_TOO_MANY_REQUESTS,
    retryTimeKnown
      ? `Spotify request limit reached (HTTP 429). Try again in ${remaining} (after ${new Date(retryAt).toLocaleString()}). Requests are paused until then.`
      : `Spotify request limit reached (HTTP 429). Spotify's retry time is not available to this browser. Requests are paused locally for ${remaining}; Spotify may require a longer wait.`
  )
  if (rateLimitDetails) {
    const { receivedAt, endpoint, reason, message } = rateLimitDetails
    error.message += ` Last Spotify 429 received: ${new Date(receivedAt).toLocaleString()}. Request: ${endpoint}.`
    if (reason) error.message += ` Spotify reason: ${reason}.`
    if (message) error.message += ` Spotify message: ${message}`
  } else {
    error.message +=
      ' Details of the original Spotify response were not saved by the previous version.'
  }
  if (local) error.message += ' Saved cooldown: no new Spotify request was sent.'
  error.details = rateLimitDetails
  error.retryTimeKnown = retryTimeKnown
  error.retryAt = retryAt
  error.retryAfter = seconds
  return error
}

/** Fail locally instead of sending requests during Spotify's cooldown. */
export function checkRateLimit() {
  if (readRetryAt() > Date.now()) throw rateLimitError(true)
}

function saveRateLimit() {
  try {
    localStorage.setItem(
      COOLDOWN_KEY,
      JSON.stringify({ retryAt, retryTimeKnown, details: rateLimitDetails })
    )
  } catch {}
}

/**
 * @param {string | null} value
 * @param {{ receivedAt: number, endpoint: string, reason?: string, message?: string }} details
 */
function setRateLimit(value, details) {
  const now = Date.now()
  let seconds = value === null || value.trim() === '' ? NaN : Number(value)
  if (!Number.isFinite(seconds)) seconds = (Date.parse(value || '') - now) / 1000
  const known = Number.isFinite(seconds) && seconds >= 0
  if (!known) seconds = DEFAULT_RETRY_SECONDS
  const nextRetryAt = now + Math.max(1, seconds) * 1000
  if (nextRetryAt >= readRetryAt()) {
    retryAt = nextRetryAt
    retryTimeKnown = known
    rateLimitDetails = details
  }
  saveRateLimit()
}

/**
 * Represents an error encountered during data fetching
 */
export class FetchError extends Error {
  /**
   * @param {number} status
   * @param {string} [message]
   */
  constructor(status, message) {
    super(message)
    this.name = 'FetchError'
    this.status = status
  }
}

/**
 * Return current user
 *
 * @param {string} token
 * @param {AbortSignal} [signal]
 */
export async function getUser(token, signal) {
  /** @type {SpotifyUser} */
  const userResponse = await get(apiUrl('me'), token, signal)
  return { ...buildUser(userResponse), country: userResponse.country }
}

/**
 * Return current user's followed artists page
 *
 * @type {CursorPagedRequest<SpotifyArtist>}
 */
export async function getUserFollowedArtistsPage(token, limit, after, signal) {
  const params = new URLSearchParams({ type: 'artist', limit: limit.toString() })
  if (after) params.set('after', after)
  /** @type {{ artists: CursorPaged<SpotifyArtist> }} */
  const response = await get(apiUrl(`me/following?${params}`), token, signal)
  return response.artists
}

/**
 * Return saved tracks page
 *
 * @type {PagedRequest<SpotifySavedTrack>}
 */
export function getUserSavedTracksPage(token, limit, offset, signal) {
  const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
  return get(apiUrl(`me/tracks?${params}`), token, signal)
}

/**
 * Return saved albums page
 *
 * @type {PagedRequest<SpotifySavedAlbum>}
 */
export function getUserSavedAlbumsPage(token, limit, offset, signal) {
  const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
  return get(apiUrl(`me/albums?${params}`), token, signal)
}

/**
 * Return saved playlists
 *
 * @type {PagedRequest<SpotifyPlaylist>}
 */
export function getUserSavedPlaylistsPage(token, limit, offset, signal) {
  const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
  return get(apiUrl(`me/playlists?${params}`), token, signal)
}

/**
 * Return an artist's albums
 *
 * @param {string} token
 * @param {string} artistId
 * @param {AlbumGroup[]} groups
 * @param {AbortSignal} [signal]
 */
export async function getArtistAlbums(token, artistId, groups, signal, session) {
  /** @type {AlbumRaw[]} */
  const albums = []
  const params = new URLSearchParams({ limit: '10', include_groups: [...groups].sort().join(',') })
  let next = apiUrl(`artists/${artistId}/albums?${params}`)

  while (next) {
    /** @type {Paged<SpotifyAlbum>} */
    const response = await getAlbumPage(next, token, signal, session)
    for (const item of response.items) albums.push(buildAlbumRaw(item, artistId))
    next = response.next
  }

  return albums
}

// Worker arguments keep AbortSignal last; callers without a session retain normal behavior.
export function getArtistAlbumsResumable(token, artistId, groups, session, signal) {
  return getArtistAlbums(token, artistId, groups, signal, session)
}

async function getAlbumPage(endpoint, token, signal, session) {
  if (signal?.aborted) throw new DOMException('Scan cancelled', 'AbortError')
  if (!session) return get(endpoint, token, signal)
  const saved = await readAlbumPage(session, endpoint)
  if (saved) return saved
  const response = await get(endpoint, token, signal)
  // Commit every successful page before requesting the next one, even if cancelled meanwhile.
  await saveAlbumPage(session, endpoint, response)
  return response
}

export async function getFullAlbumsResumable(token, albumIds, session, signal) {
  const params = new URLSearchParams({ ids: [...albumIds].sort().join(',') })
  const response = await getAlbumPage(apiUrl(`albums?${params}`), token, signal, session)
  return response.albums
}

/**
 * Return an album's track IDs
 *
 * @param {string} token
 * @param {string[]} albumIds
 * @param {AbortSignal} [signal]
 */
export async function getFullAlbums(token, albumIds, signal) {
  const params = new URLSearchParams({ ids: albumIds.join(',') })
  /** @type {{ albums: SpotifyAlbumFull[] }} */
  const response = await get(apiUrl(`albums?${params}`), token, signal)
  return response.albums
}

/**
 * Return an album's track IDs
 *
 * @param {string} token
 * @param {string[]} albumIds
 * @param {AbortSignal} [signal]
 */
export async function getAlbumsTrackIds(token, albumIds, signal) {
  /** @type {string[]} */
  const trackIds = []
  const albums = await getFullAlbums(token, albumIds, signal)

  for (const album of albums) {
    if (!album) continue

    const albumTrackIds = album.tracks.items.map((track) => track.id)
    let next = album.tracks.next

    while (next) {
      /** @type {Paged<SpotifyTrack>} */
      const response = await get(next, token, signal)
      for (const track of response.items) albumTrackIds.push(track.id)
      next = response.next
    }

    for (const id of albumTrackIds) trackIds.push(id)
  }

  return trackIds
}

/**
 * Create a new playlist
 *
 * @param {string} token
 * @param {string} userId
 * @param {PlaylistForm} form
 * @param {AbortSignal} [signal]
 * @returns {Promise<SpotifyPlaylist>}
 */
export function createPlaylist(token, userId, form, signal) {
  return post(
    apiUrl(`users/${userId}/playlists`),
    token,
    { name: form.name, description: form.description, public: form.isPublic },
    signal
  )
}

/**
 * Add tracks to an existing playlist
 *
 * @param {string} token
 * @param {string} playlistId
 * @param {string[]} trackUris
 * @param {AbortSignal} [signal]
 * @returns {Promise<SpotifyPlaylistSnapshot>}
 */
export function addTracksToPlaylist(token, playlistId, trackUris, signal) {
  return post(apiUrl(`playlists/${playlistId}/tracks`), token, { uris: trackUris }, signal)
}

/**
 * Clears all tracks from playlist
 *
 * @param {string} token
 * @param {string} playlistId
 * @param {AbortSignal} [signal]
 * @returns {Promise<SpotifyPlaylistSnapshot>}
 */
export function clearPlaylist(token, playlistId, signal) {
  return put(
    apiUrl(`playlists/${playlistId}/tracks`),
    token,
    { uris: [], range_start: 0, range_length: 99999 },
    signal
  )
}

/**
 * Create full API url
 *
 * @param {string} endpoint
 */
function apiUrl(endpoint) {
  return `${API_URL}/${endpoint}`
}

/**
 * Fire GET request
 *
 * @template T
 * @param {string} endpoint
 * @param {string} token
 * @param {AbortSignal} [signal]
 * @returns {Promise<T>}
 */
function get(endpoint, token, signal) {
  return request({ endpoint, token, signal, method: 'GET' })
}

/**
 * Fire POST request
 *
 * @template T
 * @param {string} endpoint
 * @param {string} token
 * @param {Record<string, unknown>} body
 * @param {AbortSignal} [signal]
 * @returns {Promise<T>}
 */
function post(endpoint, token, body, signal) {
  return request({
    endpoint,
    token,
    signal,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
}

/**
 * Fire PUT request
 *
 * @template T
 * @param {string} endpoint
 * @param {string} token
 * @param {Record<string, unknown>} body
 * @param {AbortSignal} [signal]
 * @returns {Promise<T>}
 */
function put(endpoint, token, body, signal) {
  return request({
    endpoint,
    token,
    signal,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
}

/**
 * Spotify API request wrapper
 *
 * @template T
 * @param {{
 *   endpoint: string
 *   token: string
 *   method: import('workbox-routing/utils/constants').HTTPMethod
 *   headers?: Record<string, string>
 *   body?: string
 *   signal?: AbortSignal
 * }} payload
 * @returns {Promise<T>}
 */
async function request(payload) {
  checkRateLimit()
  const { endpoint, token, method, headers = {}, body, signal } = payload
  const defaultHeaders = { authorization: `Bearer ${token}`, accept: 'application/json' }

  // Spotify also returns absolute URLs for subsequent pages.
  const requestUrl =
    API_PROXY && endpoint.startsWith(`${API_URL}/`)
      ? `${API_PROXY.replace(/\/$/, '')}${endpoint.slice(API_URL.length)}`
      : endpoint
  const response = await fetch(requestUrl, {
    credentials: 'omit',
    headers: { ...defaultHeaders, ...headers },
    method,
    body,
    signal,
  })

  if (response.ok) return response.json()

  if (response.status === HTTP_TOO_MANY_REQUESTS) {
    const details = {
      receivedAt: Date.now(),
      endpoint: `${method} ${endpoint.slice(API_URL.length).split('?')[0]}`,
    }
    // Block new requests immediately, before reading a potentially slow error body.
    setRateLimit(response.headers.get('Retry-After'), details)
    try {
      const json = await response.json()
      const reason = json?.error?.reason ?? json?.reason
      const message = json?.error?.message
      if (typeof reason === 'string') details.reason = reason.slice(0, 100)
      if (typeof message === 'string') details.message = message.slice(0, 300)
    } catch {}
    // Another in-flight 429 may have established a longer cooldown in the meantime.
    if (
      rateLimitDetails?.receivedAt === details.receivedAt &&
      rateLimitDetails?.endpoint === details.endpoint
    ) {
      rateLimitDetails = details
      saveRateLimit()
    }
    throw rateLimitError()
  }

  let message = `HTTP Error ${response.status}`

  try {
    const json = await response.json()
    if (json.error?.message) message = json.error.message
  } catch {}

  throw new FetchError(
    response.status,
    `${message} (HTTP ${response.status}; ${method} ${endpoint.slice(API_URL.length).split('?')[0]})`
  )
}
